import DeleteConfirmModal, {
  DeleteConfirmModalType,
} from '@/components/common/DeleteConfirmModal/DeleteConfirmModal';
import { getCardBrandIcon } from '@/services/CommonService';
import { UserService } from '@/services/UserService';
import { userData } from '@/store/Reducer/User/UserReducer';
import { useAppDispatch, useAppSelector } from '@/store/StoreHooks';
import { addModalOpenClass, removeModalOpenClass } from '@/utils/helper';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useEffect, useRef, useState } from 'react';
import { STRIPE_CONFIG } from '../../../config/stripe';
import PaymentForm, {
  PaymentFormRef,
} from '../../common/PaymentForm/PaymentForm';
import './PaymentMethod.scss';

function PaymentMethod() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.userDetail);
  const [cards, setCards] = useState<any[]>([]);
  const [showAddCard, setShowAddCard] = useState(false);
  const [loading, setLoading] = useState(false);
  const [stripePromise, setStripePromise] = useState<any>(null);
  const [customerEmail, setCustomerEmail] = useState<string>('');
  const [cardValid, setCardValid] = useState(false);
  const [cardEmpty, setCardEmpty] = useState(true);
  const paymentFormRef = useRef<PaymentFormRef>(null);
  const [submitted, setSubmitted] = useState(false);
  const [checkBoxesData, setCheckBoxesData] = useState({
    acknowledgement: false,
    authorization: false,
  });

  const [showConfirmModal, setShowConfirmModal] =
    useState<DeleteConfirmModalType>({
      open: false,
      message: '',
      onConfirm: () => {},
      onCancel: () => {},
    });

  // Load existing payment methods
  useEffect(() => {
    loadPaymentMethods();
  }, []);

  useEffect(() => {
    setCustomerEmail(user?.email || '');
  }, [user]);

  const loadPaymentMethods = async () => {
    try {
      const response = await UserService.getPaymentMethods();
      const paymentMethods = response.data || [];
      setCards(paymentMethods);
    } catch (error) {
      console.error('Error loading payment methods:', error);
    }
  };

  useEffect(() => {
    if (STRIPE_CONFIG.publishableKey) {
      setStripePromise(loadStripe(STRIPE_CONFIG.publishableKey));
    }
    if (user.member_payment_methods.length === 0) {
      setShowAddCard(true);
    }
  }, []);

  useEffect(() => {
    if (showAddCard) {
      addModalOpenClass();
    } else {
      removeModalOpenClass();
    }
  }, [showAddCard]);

  const handleSetDefault = async (cardId: string) => {
    try {
      setShowConfirmModal({
        open: true,
        message: 'Are you sure you want to set this card as default?',
        onConfirm: async () => {
          const response = await UserService.setDefaultCard(cardId);
          if (response.data) {
            // Update local state
            setCards((prevCards) =>
              prevCards.map((card) => ({
                ...card,
                is_default: card.id === cardId,
              }))
            );
            setShowConfirmModal({
              open: false,
              message: '',
              onConfirm: () => {},
              onCancel: () => {},
            });
          }
        },
        onCancel: () => {
          setShowConfirmModal({
            open: false,
            message: '',
            onConfirm: () => {},
            onCancel: () => {},
          });
        },
      });
    } catch (error) {
      setShowConfirmModal({
        open: false,
        message: '',
        onConfirm: () => {},
        onCancel: () => {},
      });
    }
  };

  const handleDeleteCard = async (cardId: string) => {
    setShowConfirmModal({
      open: true,
      message: 'Are you sure you want to delete this card?',
      onConfirm: async () => {
        try {
          const response = await UserService.deletePaymentMethod(cardId);
          if (response) {
            setCards((prevCards) =>
              prevCards.filter((card) => card.id !== cardId)
            );
            setShowConfirmModal({
              open: false,
              message: '',
              onConfirm: () => {},
              onCancel: () => {},
            });
          }
        } catch (error) {
          setShowConfirmModal({
            open: false,
            message: '',
            onConfirm: () => {},
            onCancel: () => {},
          });
        }
      },
      onCancel: () => {
        setShowConfirmModal({
          open: false,
          message: '',
          onConfirm: () => {},
          onCancel: () => {},
        });
      },
    });
  };

  const handleAddCard = async () => {
    if (!paymentFormRef.current) return;

    setSubmitted(true);
    if (
      loading ||
      !cardValid ||
      cardEmpty ||
      !checkBoxesData.acknowledgement ||
      !checkBoxesData.authorization
    ) {
      return;
    }

    setLoading(true);
    try {
      const result = await paymentFormRef.current.createPaymentMethod();

      if (result?.error) {
        console.error('Error creating payment method:', result.error);
        return;
      }

      if (result?.paymentMethod) {
        const res = await UserService.addPaymentMethod({
          payment_method: result.paymentMethod.id,
          ...checkBoxesData,
        });
        if (res.data.data) {
          setCards((prevCards) => [...prevCards, res.data.data]);
          setShowAddCard(false);
          setCardValid(false);
          setCardEmpty(true);
          setLoading(false);
          setCheckBoxesData({
            acknowledgement: false,
            authorization: false,
          });
          setSubmitted(false);
          if (user.member_payment_methods.length === 0) {
            dispatch(
              userData({ ...user, member_payment_methods: [res.data.data] })
            );
          }
        }
      }
    } catch (error) {
      console.error('Error adding card:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatExpiryDate = (month: number, year: number) => {
    return `${month.toString().padStart(2, '0')}/${year.toString().slice(-2)}`;
  };

  return (
    <div className="profile-main">
      <div className="profile-content">
        <div className="payment-methods-section">
          <div className="head-profile">
            <h1 className="page-title">Payment Methods</h1>
            <button
              type="button"
              className="btn btn--sm btn--pri"
              onClick={() => setShowAddCard(true)}
              disabled={loading}
            >
              ADD CARD
            </button>
          </div>

          {cards.length === 0 ? (
            <div className="no-record-found">
              <i className="cs-icon icon-credit-card"></i>
              <p>No payment methods found.</p>
            </div>
          ) : (
            <div className="cards-grid">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className={`card-item ${card.is_default ? 'default' : ''}`}
                >
                  <div className="card-header">
                    <div className="card-brand">
                      {getCardBrandIcon(card.card_details.brand)}
                      {/* <span className="brand-name">{card.card_details.brand.toUpperCase()}</span> */}
                    </div>
                    {card.is_default && (
                      <span className="default-badge">
                        <i className="cs-icon icon-check"></i>
                        Default
                      </span>
                    )}
                  </div>

                  <div className="card-body">
                    <div className="card-number">
                      •••• •••• •••• {card.card_details.last4}
                    </div>
                    <div className="card-details">
                      <span className="cardholder-name">
                        {card.billing_details.name}
                      </span>
                      <span className="expiry-date">
                        Expires{' '}
                        {formatExpiryDate(
                          card.card_details.exp_month,
                          card.card_details.exp_year
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="card-actions">
                    {!card.is_default && (
                      <button
                        className="set-default-btn"
                        onClick={() => handleSetDefault(card.id)}
                        disabled={loading}
                      >
                        Set as Default
                      </button>
                    )}
                    {!card.is_default && (
                      <button
                        className="btn-icon"
                        onClick={() => handleDeleteCard(card.id)}
                        disabled={loading}
                      >
                        <i className="cs-icon icon-trash"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add New Card Section */}
        {showAddCard && (
          <div className="modal-overlay">
            <div className="modal-center">
              <div
                className="modal-wrapper"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 className="modal-title">PAYMENT METHOD</h2>
                <div className="add-card-section">
                  {stripePromise && (
                    <Elements stripe={stripePromise}>
                      <PaymentForm
                        showCardForm={true}
                        onCardValid={(valid, cardName) => {
                          setCardValid(valid);
                        }}
                        isCardEmpty={(isEmpty) => {
                          setCardEmpty(isEmpty);
                        }}
                        isSubmitted={submitted}
                        customerEmail={customerEmail}
                        disabled={loading}
                        ref={paymentFormRef}
                        checkBoxesData={checkBoxesData}
                        onCheckBoxChange={(name, value) => {
                          setCheckBoxesData((prev) => ({
                            ...prev,
                            [name]: value,
                          }));
                        }}
                      />
                      <div className="add-card-actions">
                        <button
                          className="save-card-btn"
                          disabled={loading}
                          onClick={handleAddCard}
                        >
                          {loading ? 'Saving...' : 'Save Card'}
                        </button>
                        <button
                          className="cancel-btn"
                          onClick={() => {
                            setShowAddCard(false);
                            setCheckBoxesData({
                              acknowledgement: false,
                              authorization: false,
                            });
                            setSubmitted(false);
                          }}
                          disabled={loading}
                        >
                          Cancel
                        </button>
                      </div>
                    </Elements>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <DeleteConfirmModal
        isOpen={showConfirmModal.open}
        message={showConfirmModal.message}
        onConfirm={showConfirmModal.onConfirm}
        onCancel={showConfirmModal.onCancel}
      />
    </div>
  );
}

export default PaymentMethod;
